import * as THREE from 'three';
import Application from '../Application';
import ComputerSetup from './Computer';
import MonitorScreen from './MonitorScreen';
import Environment from './Environment';
import { reveal } from '../Reveal';
import { addDissolve, addPaperFade, dissolveUniforms } from './Dissolve';
const OUTSIDE_FROM = 0.55; // part of the room's reveal after which the outside starts fading in
export default class World {
    constructor() {
        // the loading reveal (see Reveal.ts)
        this.deskFades = [];
        this.deskOnly = []; // monitor meshes that can't fade (the hole the screen page shows through)
        this.background = [];
        this.outsideFade = { value: 1 };
        this.applied = { desk: -1, background: -1 };
        this.application = new Application();
        this.scene = this.application.scene;
        this.resources = this.application.resources;
        // Wait for resources
        this.resources.on('ready', () => {
            // Setup
            this.environment = new Environment();
            this.computerSetup = new ComputerSetup();
            const before = new Set(this.scene.children);
            this.monitorScreen = new MonitorScreen();
            const monitor = this.scene.children.filter((o) => !before.has(o));
            this.setReveal(monitor);
        });
    }
    setReveal(monitor) {
        const fade = (m) => ({ material: m, base: m.opacity, transparent: m.transparent });
        const { gltfModel } = this.resources.items;
        // desk: the terminal, the chair (its own copy of the props material) and the monitor's screen layers
        this.deskFades.push(fade(this.computerSetup.bakedModel.material));
        for (const o of monitor) {
            o.traverse((c) => {
                if (!(c instanceof THREE.Mesh) || c === this.monitorScreen.dimmingPlane)
                    return; // set every frame: see update()
                if (c.material.blending === THREE.NoBlending)
                    this.deskOnly.push(c);
                else
                    this.deskFades.push(fade(c.material));
            });
        }
        // with the desk: the chair and the small things on top of the desk (books, notebook, tape reel...), each on its
        // own copy of the props material so they fade with the desk; every other prop dissolves in with the room
        this.scene.updateMatrixWorld(true);
        const deskBox = new THREE.Box3().setFromObject(this.computerSetup.bakedModel.getModel());
        const deskSize = deskBox.getSize(new THREE.Vector3());
        const onDesk = (o) => {
            const c = new THREE.Box3().setFromObject(o).getCenter(new THREE.Vector3());
            return c.x > deskBox.min.x && c.x < deskBox.max.x && c.z > deskBox.min.z && c.z < deskBox.max.z
                && c.y > deskBox.min.y + 0.55 * deskSize.y;
        };
        const copies = new Map();
        const withDesk = [];
        for (const root of [gltfModel.propsModel.scene, gltfModel.glassModel.scene]) {
            for (const piece of root.children) {
                if (!piece.name.startsWith('chair') && !onDesk(piece))
                    continue;
                withDesk.push(piece.name);
                piece.traverse((c) => {
                    if (!(c instanceof THREE.Mesh))
                        return;
                    if (!copies.has(c.material)) {
                        const m = c.material.clone();
                        copies.set(c.material, m);
                        this.deskFades.push(fade(m));
                    }
                    c.material = copies.get(c.material);
                });
            }
        }
        console.debug('[reveal] with the desk:', withDesk.join(', '));
        reveal.ready = true;
        const desk = deskBox.getCenter(new THREE.Vector3());
        // everything else (room shell, rock face, sea and sky, the other props and the reel's case if it isn't on
        // the desk) dissolves in with a shader (Dissolve.ts), growing from the desk
        const bounds = new THREE.Box3();
        // (the sea and sky outside the windows only fade in, at the end)
        const outside = this.environment.bakedModels[2].material;
        this.outside = outside;
        this.outsideFade = addPaperFade(outside);
        const rest = new Set(this.environment.bakedModels.map((b) => b.material).filter((m) => m !== outside));
        gltfModel.glassModel.scene.traverse((c) => { if (c instanceof THREE.Mesh && !this.deskFades.some((f) => f.material === c.material))
            rest.add(c.material); });
        for (const m of rest) {
            addDissolve(m);
            this.background.push(m);
        }
        bounds.setFromObject(this.environment.bakedModels[0].getModel()); // the room shell sets the reach
        dissolveUniforms.uOrigin.value.copy(desk);
        const corners = [bounds.min, bounds.max].flatMap((a) => [bounds.min, bounds.max].flatMap((b) => [bounds.min, bounds.max].map((c) => new THREE.Vector3(a.x, b.y, c.z))));
        // the shell's far corners are well outside the view; this much of that reach covers what the camera sees
        dissolveUniforms.uRadius.value = 0.4 * Math.max(...corners.map((c) => c.distanceTo(desk)));
    }
    applyReveal() {
        const setFades = (fades, t) => {
            for (const { material, base, transparent: own } of fades) {
                // see-through only while fading; afterwards back to the material's own setting, so solid things stay
                // in the solid pass (left see-through, they were re-sorted every frame and flickered over the screen)
                const transparent = t < 1 || own;
                if (material.transparent !== transparent) {
                    material.transparent = transparent;
                    material.needsUpdate = true;
                }
                material.opacity = base * t;
                material.visible = t > 0;
            }
        };
        const { desk, background } = reveal;
        if (desk !== this.applied.desk) {
            setFades(this.deskFades, desk);
            this.deskOnly.forEach((o) => (o.visible = desk > 0));
            const css = document.getElementById('css');
            if (css)
                css.style.opacity = desk >= 1 ? '' : String(desk);
            this.applied.desk = desk;
        }
        if (background !== this.applied.background) {
            dissolveUniforms.uReveal.value = background;
            this.background.forEach((m) => (m.visible = background > 0));
            const o = Math.min(1, Math.max(0, (background - OUTSIDE_FROM) / (1 - OUTSIDE_FROM)));
            this.outsideFade.value = o;
            this.outside.visible = o > 0;
            this.applied.background = background;
        }
    }
    update() {
        if (this.deskFades.length)
            this.applyReveal();
        if (this.monitorScreen) {
            this.monitorScreen.update();
            const dim = this.monitorScreen.dimmingPlane;
            if (dim && reveal.desk < 1) {
                // @ts-ignore
                dim.material.opacity *= reveal.desk;
                dim.visible = reveal.desk > 0;
            }
        }
        if (this.environment)
            this.environment.update();
    }
}
